import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import ConfirmActionDialog from "@/components/common/ConfirmActionDialog";
import ImageUpload from "@/components/common/ImageUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const input = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand";

export function Section({ title, subtitle, actions, children }) {
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tighter">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

export function StatCard({ label, value, hint, accent }) {
  return (
    <div className={`rounded-lg border p-5 ${accent ? "border-brand/40 bg-primary text-primary-foreground" : "border-border bg-card"}`} data-testid={`stat-${label}`}>
      <p className={`text-xs uppercase tracking-wide ${accent ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{label}</p>
      <p className={`mt-2 font-mono text-2xl font-bold ${accent ? "text-brand" : ""}`}>{value}</p>
      {hint && <p className={`mt-1 text-xs ${accent ? "text-primary-foreground/50" : "text-muted-foreground"}`}>{hint}</p>}
    </div>
  );
}

export function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-brand" : "bg-border"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export function Field({ f, value, onChange }) {
  const t = useT();
  const label = t(`admin.f.${f.key}`);
  if (f.type === "checkbox") {
    return (
      <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
        {label}
        <Toggle checked={!!value} onChange={(v) => onChange(f.key, v)} testid={`field-${f.key}`} />
      </label>
    );
  }
  if (f.type === "select") {
    return (
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
        <select className={input} value={value ?? ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`field-${f.key}`}>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (f.type === "image") {
    return (
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
        <ImageUpload value={value || ""} onChange={(v) => onChange(f.key, v || "")} testid={`field-${f.key}`} />
      </div>
    );
  }
  if (f.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
        <textarea className={`${input} h-20 py-2`} value={value ?? ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`field-${f.key}`} />
      </div>
    );
  }
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      <input type={f.type === "number" ? "number" : f.type === "color" ? "color" : "text"} className={input}
        value={value ?? ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`field-${f.key}`} />
    </div>
  );
}

// Generic config CRUD backed by /admin/config/{resource}
export function ConfigResource({ resource, titleKey, subtitleKey, columns, fields, defaults }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const title = t(titleKey);
  const load = () => api.get(`/admin/config/${resource}`).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(defaults); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...defaults, ...it }); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.key] = parseFloat(payload[f.key]) || 0; });
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/config/${resource}/${editing.id}`, payload);
      else await api.post(`/admin/config/${resource}`, payload);
      toast.success(t("admin.saved"));
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    await api.delete(`/admin/config/${resource}/${confirmDel.id}`);
    toast.success(t("admin.deleted"));
    load();
  };

  return (
    <Section title={title} subtitle={t(subtitleKey)} actions={
      <button onClick={openNew} data-testid={`${resource}-add`} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
        <Plus className="h-4 w-4" />{t("admin.add")}
      </button>
    }>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid={`${resource}-table`}>
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{columns.map((c) => <th key={c.key} className="p-3 font-medium">{t(c.labelKey)}</th>)}<th className="p-3 text-right">{t("admin.actions")}</th></tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="p-8 text-center text-muted-foreground" data-testid={`${resource}-empty`}>{t("admin.empty")}</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`${resource}-row-${it.id}`}>
                {columns.map((c) => <td key={c.key} className="p-3">{c.render ? c.render(it, t) : String(it[c.key] ?? "—")}</td>)}
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(it)} data-testid={`${resource}-edit-${it.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setConfirmDel(it)} data-testid={`${resource}-delete-${it.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`${resource}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? t("admin.resource.edit", { title }) : t("admin.resource.new", { title })}</DialogTitle>
            <DialogDescription>{t(subtitleKey)}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => <Field key={f.key} f={f} value={form[f.key]} onChange={set} />)}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">{t("admin.cancel")}</button>
            <button onClick={save} disabled={saving} data-testid={`${resource}-save`} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">{saving ? t("common.confirm.processing") : t("admin.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={t("admin.confirm.deleteItemTitle")}
        description={t("admin.confirm.deleteItemDesc")}
        resourceName={confirmDel?.name || confirmDel?.label || confirmDel?.task}
        onConfirm={remove}
      />
    </Section>
  );
}

// Generic settings form backed by a single GET/PUT endpoint
export function SettingsForm({ endpoint, titleKey, subtitleKey, fields, testid }) {
  const t = useT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(endpoint).then(({ data }) => setForm(data || {})).catch(() => setForm({}));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put(endpoint, form); toast.success(t("admin.saved")); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); }
    finally { setSaving(false); }
  };
  if (!form) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  return (
    <Section title={t(titleKey)} subtitle={t(subtitleKey)} actions={
      <button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">{saving ? t("common.confirm.processing") : t("admin.save")}</button>
    }>
      <div className="grid gap-3 rounded-lg border border-border bg-card p-6 sm:grid-cols-2">
        {fields.map((f) => <Field key={f.key} f={f} value={form[f.key]} onChange={set} />)}
      </div>
    </Section>
  );
}
